import { useEffect, useRef, useState } from "react";
import { getDiaryOverview } from "../../../services/WeeklyDiaryService";
import { handleApiError } from "../../../utils/errorHandler";
import { SEARCH_DEBOUNCE_MS } from "../WeeklyDiaryConstant";
import { normalizeDiaryOverview } from "../utils/diaryUtils";

const EMPTY = {
  weekStart: "",
  weekEnd: "",
  weekLabel: "",
  rows: [],
  summary: {
    totalLessonPlans: 0,
    diariesExpected: 0,
    diariesSubmitted: 0,
    diariesApproved: 0,
    submittedPercentage: 0,
    overdue: 0,
    avgCompletion: 0,
  },
};

/**
 * Weekly Diary overview: one row per teacher + subject.
 * No `week_start` is sent; the API returns its default (current) week and labels it (`week_label`).
 */
export const useDiaryOverview = () => {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [overview, setOverview] = useState(EMPTY);
  const [isLoading, setIsLoading] = useState(false);

  // Ignore responses that arrive after a newer request
  const requestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setIsLoading(true);

    getDiaryOverview({ status, search: debouncedSearch })
      .then((response) => {
        if (requestId === requestRef.current) setOverview(normalizeDiaryOverview(response));
      })
      .catch((error) => {
        if (requestId === requestRef.current) {
          setOverview(EMPTY);
          handleApiError(error);
        }
      })
      .finally(() => {
        if (requestId === requestRef.current) setIsLoading(false);
      });
  }, [status, debouncedSearch]);

  return { ...overview, status, setStatus, search, setSearch, isLoading };
};
